import * as React from "react";
import { useLingui } from "@lingui/react/macro";
import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../hooks/use-reduced-motion";
import {
  ActionFieldEditor,
  type ActionFieldEditorProps,
  type ActionFieldSpec,
  type ActionFieldValue,
  validateField,
} from "./ActionFieldEditor";
import { seedActionValues } from "./actionFieldSeed";
import { ActionDrawerHeader } from "./action-drawer/ActionDrawerHeader";
import { ActionDrawerReasoning } from "./action-drawer/ActionDrawerReasoning";
import { ActionDrawerFooter } from "./action-drawer/ActionDrawerFooter";
import {
  type ActionDrawerEvent,
  type ActionDrawerState,
  type ActionDrawerStatus,
  INITIAL_ACTION_STATE,
  actionDrawerReducer,
} from "./action-drawer/types";

/**
 * ActionDrawer — Action Proposal Drawer (A.7).
 *
 * UX cardine: NOT a floating right-rail Drawer like `ActionForm.tsx` —
 * this component is a **persistent surface that mounts at the top of the
 * routed workspace tab** (Finanza, Conversazioni, Calendario, …). It stays
 * visible until the user confirms, rejects, or saves a draft.
 *
 * Replaces v1's `ActionProposalCard.tsx` (1505 LOC monolith) — split into 5
 * zoned subcomponents:
 *   1. Header  (vendor icon + title + status badge)        ← ActionDrawerHeader
 *   2. Fields  (schema-driven, editable)                   ← ActionFieldEditor
 *   3. Reasoning (collapsable trace)                       ← ActionDrawerReasoning
 *   4. Preview (vendor-specific slot)                      ← children / `preview`
 *   5. Footer  (Rifiuta · Salva bozza · Conferma)          ← ActionDrawerFooter
 *
 * State machine: `proposed → editing → confirming → executing → executed | error`.
 *
 * Per `feedback_hitl_card_editable_fields.md` — every AI-proposed field
 * MUST be editable; the form audit-logs `proposed_fields` + `executed_fields`
 * + diff downstream via the parent's `onConfirm({ executedFields })`.
 */

/**
 * Context handed to a controlled preview render-prop so it can render
 * editable inputs inline (envelope-style for email, calendar-card-style
 * for meetings, …). See :prop:`ActionDrawerProps.preview`.
 */
export interface PreviewControlledCtx {
  readonly schema: readonly ActionFieldSpec[];
  readonly values: Readonly<Record<string, ActionFieldValue>>;
  readonly errors: Readonly<Record<string, { message: string } | undefined>>;
  readonly onChange: (next: Record<string, ActionFieldValue>) => void;
  readonly disabled: boolean;
}

export interface ActionDrawerProps {
  /** Vendor + action identification. */
  vendorIcon?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Schema describing editable fields. */
  schema: readonly ActionFieldSpec[];
  /** What Ando proposed — immutable reference set. */
  proposedFields: Readonly<Record<string, ActionFieldValue>>;
  /** Optional: rehydrate a saved-draft / executed state. */
  initialEditedFields?: Readonly<Record<string, ActionFieldValue>>;
  /** Status — controlled by parent OR uncontrolled (uses internal reducer). */
  status?: ActionDrawerStatus;
  /** Error message when status is `error`. */
  error?: string | null;
  /** Reasoning trace lines. */
  reasoning?: readonly string[];
  /** Source citations referenced from reasoning. */
  citations?: ReadonlyArray<{ id: string; label: string }>;
  /**
   * Vendor-specific preview slot. Two flavours:
   *
   * 1. ``React.ReactNode`` — static display node (legacy callers like
   *    ``FicCreateInvoicePreview``). Rendered below the stacked
   *    ``ActionFieldEditor`` in a separate "Anteprima" card.
   * 2. ``(ctx) => React.ReactNode`` — inline editor mode. The preview
   *    receives the live ``values`` / ``onChange`` / ``errors`` so it
   *    can render envelope-style inputs in place (Gmail-style for
   *    ``EmailSendDraftPreview``). When this form is used together with
   *    :prop:`previewOwnedFields`, the stacked editor skips those fields
   *    so the user never sees the same input twice.
   */
  preview?: React.ReactNode | ((ctx: PreviewControlledCtx) => React.ReactNode);
  /**
   * Names of fields that the ``preview`` slot edits inline. Listed names
   * are filtered out of the stacked ``ActionFieldEditor`` form so the
   * user never sees the same input twice (the inline preview owns them).
   * May 26, 2026 — added with the ``EmailSendDraftPreview`` envelope
   * editor rewrite to drop the duplicated form-on-top-mock-below layout.
   */
  previewOwnedFields?: ReadonlyArray<string>;
  /**
   * Async typeahead resolver for ``reference`` fields (CRM record lookups
   * etc.). Forwarded verbatim to the inner ``<ActionFieldEditor>``.
   * Optional — when omitted the editor degrades to free-text input.
   */
  loadReferenceOptions?: ActionFieldEditorProps["loadReferenceOptions"];
  /**
   * Async people-search transport for ``recipients`` fields (Gmail /
   * Outlook autocomplete via ``GET /people/search``). Forwarded verbatim
   * to the inner ``<ActionFieldEditor>``. Required for calendar
   * ``attendees`` autocomplete to work end-to-end — without this prop
   * the chip input falls back to free-text mode (see
   * ``feedback_action_forms_account_picker_first.md``).
   */
  loadPeopleOptions?: ActionFieldEditorProps["loadPeopleOptions"];
  /**
   * Upload transport for ``file`` fields (email attachments etc.).
   * Forwarded verbatim to the inner ``<ActionFieldEditor>``. Optional —
   * when omitted the file picker stays read-only.
   */
  uploadAttachment?: ActionFieldEditorProps["uploadAttachment"];
  /** Forwarded to the inner editor for the "Scegli dai documenti Ando" picker. */
  searchAttachableDocuments?: ActionFieldEditorProps["searchAttachableDocuments"];
  /**
   * Optional LIVE values callback — fired on every field edit (not just on
   * save/confirm). Lets a host react to an in-progress selection, e.g. re-
   * resolving the effective email signature when the ``from_account`` picker
   * changes. Additive + inactive when omitted (no behaviour change).
   */
  onValuesChange?: (values: Record<string, ActionFieldValue>) => void;
  /** Called when user clicks Rifiuta. */
  onReject: () => void;
  /** Called when user clicks Salva bozza. */
  onSaveDraft?: (editedFields: Record<string, ActionFieldValue>) => void;
  /** Called when user clicks Esegui ora (final confirm). */
  onConfirm: (executedFields: Record<string, ActionFieldValue>) => void;
  /** Custom validator hook; falls back to `validateField`. */
  validate?: ActionFieldEditorProps["errors"] extends infer _ ? never : never;
  /**
   * When the drawer is mounted inside a richer chrome (e.g.
   * ``FocusedViewLayout`` in ``FocusedActionView``) that already
   * surfaces the icon badge + eyebrow + big title, suppressing the
   * built-in ``ActionDrawerHeader`` avoids the "double-titolo" anti-
   * pattern. The status badge migrates onto the chrome eyebrow at the
   * call site. Default ``false`` — every existing call site keeps the
   * compact in-card header. (May 24, 2026 — added with FocusedActionView.)
   */
  hideHeader?: boolean;
  /**
   * When ``true``, drop the outer card chrome (border + ring + rounded
   * + shadow + padding) so the drawer can render as a flat body inside
   * the ``FocusedViewLayout`` scroll context. The inner spacing rhythm
   * is preserved; only the box-decoration is suppressed. Used together
   * with ``hideHeader`` for the FocusedActionView mount.
   */
  unstyled?: boolean;
  /**
   * Render as an a11y dialog (``role="dialog"`` + ``aria-modal="true"``)
   * with auto-focus on the first interactive field and Escape-to-close
   * keyboard semantics. Default ``false`` — the drawer keeps its
   * inline-region role for the every-day proposal card. Enable when the
   * drawer is mounted inside a richer focused chrome (e.g.
   * ``FocusedActionView``) so screen readers announce it as a dialog
   * and assistive-tech users can dismiss with Escape.
   */
  focusedModal?: boolean;
  /**
   * Called when the user presses Escape while the drawer is in
   * ``focusedModal`` mode. Wire to the host's "close this focused view"
   * handler (typically the same ``onBack`` callback the FocusedViewLayout
   * already exposes). Ignored when ``focusedModal === false``.
   */
  onClose?: () => void;
  /**
   * When set, the final "Esegui ora" step is gated behind a type-to-confirm
   * input (``<TypedConfirmGate>``): the operator must type this phrase
   * verbatim before execute unlocks. Set for irreversible actions
   * (delete record/event/item, destructive db_write, SEPA payment).
   */
  confirmPhrase?: string;
  className?: string;
}

export const ActionDrawer: React.FC<ActionDrawerProps> = ({
  vendorIcon,
  title,
  subtitle,
  schema,
  proposedFields,
  initialEditedFields,
  status: controlledStatus,
  error: controlledError,
  reasoning,
  citations,
  preview,
  previewOwnedFields,
  loadReferenceOptions,
  loadPeopleOptions,
  uploadAttachment,
  searchAttachableDocuments,
  onValuesChange,
  onReject,
  onSaveDraft,
  onConfirm,
  hideHeader = false,
  unstyled = false,
  focusedModal = false,
  onClose,
  confirmPhrase,
  className,
}) => {
  const { i18n } = useLingui();
  const reduced = useReducedMotion();

  // ─── State machine ───────────────────────────────────────────────────────
  // Uncontrolled by default; if a caller passes `status`, we run controlled.
  const [internalState, dispatch] = React.useReducer(
    actionDrawerReducer,
    INITIAL_ACTION_STATE,
  );

  const state: ActionDrawerState = controlledStatus
    ? { status: controlledStatus, error: controlledError ?? null }
    : internalState;

  // ─── Field values ────────────────────────────────────────────────────────
  // We keep ONE live map (`values`) and diff vs `proposedFields` at confirm time.
  // ``seedActionValues`` layers schema-resolved defaults (``spec.value``, e.g. an
  // auto-selected account) under ``proposedFields`` under the user's edits — so
  // an auto-filled required field isn't reported empty. See ``actionFieldSeed``.
  const [values, setValues] = React.useState<Record<string, ActionFieldValue>>(
    () => seedActionValues(schema, proposedFields, initialEditedFields),
  );

  // Reset whenever the upstream proposal / resolved schema changes.
  React.useEffect(() => {
    setValues(seedActionValues(schema, proposedFields, initialEditedFields));
  }, [schema, proposedFields, initialEditedFields]);

  // ─── Validation (per-field) ──────────────────────────────────────────────
  // Fields a STATIC-NODE preview owns (``previewOwnedFields`` while ``preview``
  // is NOT a render-prop) are managed by the host OUTSIDE the drawer's
  // ``values`` map — e.g. ``document_create``'s ``title`` / ``slides`` live in
  // the host's slide-outline draft state, not here. Validating them against
  // the (empty) drawer ``values`` would wrongly flag the required fields as
  // "Campo obbligatorio" and gate off Conferma. Render-prop previews
  // (send_email / send_message) bind their owned fields to ``values``, so
  // those stay fully validated.
  const validatedSchema = React.useMemo(() => {
    if (typeof preview === "function" || !previewOwnedFields?.length) {
      return schema;
    }
    const owned = new Set(previewOwnedFields);
    return schema.filter((s) => !owned.has(s.name));
  }, [schema, preview, previewOwnedFields]);

  const errors = React.useMemo(() => {
    const out: Record<string, { message: string } | undefined> = {};
    for (const spec of validatedSchema) {
      out[spec.name] = validateField(spec, values[spec.name] ?? null, i18n);
    }
    return out;
  }, [validatedSchema, values, i18n]);

  const hasErrors = React.useMemo(
    () => Object.values(errors).some((e) => e !== undefined),
    [errors],
  );

  // ─── Dirty diff ──────────────────────────────────────────────────────────
  const isDirty = React.useMemo(() => {
    for (const spec of schema) {
      const a = values[spec.name] ?? null;
      const b = proposedFields[spec.name] ?? null;
      if (a !== b) return true;
    }
    return false;
  }, [schema, values, proposedFields]);

  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleChange = React.useCallback(
    (next: Record<string, ActionFieldValue>) => {
      setValues(next);
      dispatch({ kind: "edit" });
      onValuesChange?.(next);
    },
    [onValuesChange],
  );

  const handleConfirmStep = React.useCallback(() => {
    // CONTROLLED mode: parent owns the lifecycle (e.g. `FocusedActionView`
    // passes ``status`` from its `useExecuteAction` mutation). Dispatching
    // to the internal reducer is a no-op here because `state` is derived
    // from `controlledStatus` upstream. The two-tap "Confermi?" affordance
    // is unnecessary inside the dedicated detail pane (the user has
    // already drilled in), so fire `onConfirm` directly. This fixes the
    // bug where clicking Conferma did NOTHING when running controlled.
    if (controlledStatus !== undefined) {
      if (
        state.status === "proposed" ||
        state.status === "editing" ||
        state.status === "error"
      ) {
        onConfirm(values);
      }
      return;
    }
    // UNCONTROLLED mode: keep the two-tap pattern for the compact
    // ``ActionDrawer`` mount (proposed → confirming → executing).
    if (state.status === "proposed" || state.status === "editing") {
      dispatch({ kind: "confirm" });
      return;
    }
    if (state.status === "confirming") {
      dispatch({ kind: "execute_start" });
      onConfirm(values);
      return;
    }
    if (state.status === "error") {
      dispatch({ kind: "retry" });
      onConfirm(values);
      return;
    }
  }, [controlledStatus, state.status, onConfirm, values]);

  const handleCancelConfirm = React.useCallback(() => {
    dispatch({ kind: "cancel_confirm" });
  }, []);

  const handleSaveDraft = React.useCallback(() => {
    onSaveDraft?.(values);
  }, [onSaveDraft, values]);

  // ESC during editing → revert to proposed. The native Drawer/Dialog ESC
  // closes the surface; since we're embedded (not a Radix Dialog), wire a
  // local listener that only fires when the drawer root is focused.
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && state.status === "editing") {
        e.stopPropagation();
        // Reset values to proposed and rewind the state machine.
        setValues({ ...proposedFields });
        dispatch({ kind: "revert" });
      }
    };
    node.addEventListener("keydown", handler);
    return () => node.removeEventListener("keydown", handler);
  }, [proposedFields, state.status]);

  // ── Focused-modal a11y: autofocus + Escape → onClose ─────────────────
  // Only active when ``focusedModal === true``. Autofocuses the first
  // interactive element after mount so AT users land on actionable
  // content immediately (instead of having to Tab from the host shell).
  // Escape closes the surface when there's no editing-revert path to
  // take — the editing handler above wins precedence on the same key.
  React.useEffect(() => {
    if (!focusedModal) return;
    const node = rootRef.current;
    if (!node) return;
    // Autofocus on mount — picks the first standard-focusable element
    // (input / textarea / button / link / select / [tabindex]>=0). The
    // section root has tabIndex=-1 so it is itself reachable for focus
    // recovery but not part of the Tab cycle.
    const focusable = node.querySelector<HTMLElement>(
      'input:not([disabled]):not([tabindex="-1"]),' +
        'textarea:not([disabled]):not([tabindex="-1"]),' +
        'button:not([disabled]):not([tabindex="-1"]),' +
        'select:not([disabled]):not([tabindex="-1"]),' +
        'a[href]:not([tabindex="-1"]),' +
        '[tabindex]:not([tabindex="-1"])',
    );
    (focusable ?? node).focus();
    if (!onClose) return;
    const escHandler = (e: KeyboardEvent) => {
      // The editing-revert handler runs in the bubble phase on the node;
      // it ``stopPropagation``s when it acts, so this document-level
      // listener only fires for non-editing states.
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", escHandler);
    return () => document.removeEventListener("keydown", escHandler);
  }, [focusedModal, onClose]);

  const canConfirm =
    !hasErrors &&
    (state.status === "proposed" ||
      state.status === "editing" ||
      state.status === "confirming" ||
      state.status === "error");

  return (
    <motion.section
      ref={rootRef}
      tabIndex={-1}
      aria-label="Azione proposta da Ando"
      role={focusedModal ? "dialog" : "region"}
      aria-modal={focusedModal ? true : undefined}
      initial={reduced ? false : { opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduced ? 0 : 0.18, ease: [0.4, 0, 0.2, 1] }}
      className={cn(
        "flex w-full flex-col gap-4",
        // ``unstyled`` strips the card chrome (border + ring + rounded +
        // shadow + padding) so the drawer can render flat inside a
        // richer container (e.g. FocusedViewLayout scroll context).
        !unstyled &&
          "rounded-xl border border-hairline bg-card/95 p-4 shadow-sm ring-1 ring-ando-orange-soft/50",
        !unstyled && state.status === "executed" && "ring-success/30",
        !unstyled && state.status === "error" && "ring-destructive/30",
        className,
      )}
    >
      {hideHeader ? null : (
        <ActionDrawerHeader
          vendorIcon={vendorIcon}
          title={title}
          subtitle={subtitle}
          status={state.status}
        />
      )}

      {state.status === "error" && state.error ? (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive text-xs">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">Esecuzione fallita</p>
            <p className="text-ink-2">{state.error}</p>
          </div>
        </div>
      ) : null}

      {state.status === "confirming" ? (
        <div className="rounded-md border border-accent/30 bg-ando-orange-soft/40 px-3 py-2 text-ink-1 text-xs">
          Confermi l'esecuzione? Premi <strong>Esegui ora</strong> per
          procedere o <strong>Annulla</strong> per tornare alla modifica.
        </div>
      ) : null}

      {/* Filter out fields the preview owns inline so we never render the
          same input twice (Gmail-style envelope editor for send_email,
          etc.). May 26, 2026 — see previewOwnedFields docstring. */}
      {(() => {
        const ownedSet = new Set(previewOwnedFields ?? []);
        const stackedSchema =
          ownedSet.size > 0
            ? schema.filter((s) => !ownedSet.has(s.name))
            : schema;
        if (stackedSchema.length === 0) return null;
        return (
          <ActionFieldEditor
            schema={stackedSchema}
            values={values}
            errors={errors}
            onChange={handleChange}
            disabled={state.status === "executing" || state.status === "executed"}
            loadReferenceOptions={loadReferenceOptions}
            loadPeopleOptions={loadPeopleOptions}
            uploadAttachment={uploadAttachment}
            searchAttachableDocuments={searchAttachableDocuments}
          />
        );
      })()}

      {preview
        ? typeof preview === "function"
          ? // Controlled render-prop: the preview becomes the inline editor
            // (e.g. EmailSendDraftPreview envelope). No "Anteprima" wrapper —
            // the preview owns its full layout.
            preview({
              schema,
              values,
              errors,
              onChange: handleChange,
              disabled:
                state.status === "executing" || state.status === "executed",
            })
          : // Legacy static preview node — keep the "Anteprima" card.
            <div className="rounded-md border border-hairline bg-cream-deep/30 p-3">
              <p className="mb-2 font-semibold text-[10px] text-ink-3 uppercase tracking-wide">
                Anteprima
              </p>
              {preview}
            </div>
        : null}

      {reasoning && reasoning.length > 0 ? (
        <ActionDrawerReasoning steps={reasoning} citations={citations} />
      ) : null}

      <div className="border-hairline border-t pt-3">
        <ActionDrawerFooter
          status={state.status}
          canConfirm={canConfirm}
          isDirty={isDirty}
          onReject={onReject}
          onSaveDraft={onSaveDraft ? handleSaveDraft : undefined}
          onConfirm={handleConfirmStep}
          onCancelConfirm={handleCancelConfirm}
          confirmPhrase={confirmPhrase}
        />
      </div>
    </motion.section>
  );
};
ActionDrawer.displayName = "ActionDrawer";

// Re-export the reducer + status for parent surfaces that want to drive the
// state externally (e.g. when the WS event `action_status` overrides the
// local machine).
export {
  actionDrawerReducer,
  INITIAL_ACTION_STATE,
  type ActionDrawerEvent,
  type ActionDrawerState,
  type ActionDrawerStatus,
};
